import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FlashDeal {
  id: string;
  activity_id: string;
  discount_percent: number;
  title_ar: string | null;
  title_en: string | null;
  starts_at: string;
  ends_at: string;
  is_active: boolean;
  activity?: {
    id: string;
    title_ar: string;
    title_en: string | null;
    image_url: string | null;
    video_url: string | null;
    price: number;
    category_id: string | null;
  } | null;
}

const VIDEO_BUCKET = 'activity-videos';
const PUBLIC_PREFIX = `/storage/v1/object/public/${VIDEO_BUCKET}/`;

async function toPlayableVideoUrl(publicUrl: string | null): Promise<string | null> {
  if (!publicUrl) return null;
  const idx = publicUrl.indexOf(PUBLIC_PREFIX);
  if (idx === -1) return publicUrl;
  const path = publicUrl.slice(idx + PUBLIC_PREFIX.length).split('?')[0];
  const { data, error } = await supabase.storage
    .from(VIDEO_BUCKET)
    .createSignedUrl(path, 60 * 60);
  if (error || !data?.signedUrl) {
    console.warn('[flash-deals] failed to sign video url', path, error);
    return null;
  }
  return data.signedUrl;
}

export function useActiveFlashDeals() {
  return useQuery({
    queryKey: ['flash-deals', 'active'],
    queryFn: async (): Promise<FlashDeal[]> => {
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from('flash_deals')
        .select('id, activity_id, discount_percent, title_ar, title_en, starts_at, ends_at, is_active, activity:activities!flash_deals_activity_id_fkey(id, title_ar, title_en, gallery_urls, video_url, price_kwd, category_id)')
        .eq('is_active', true)
        .lte('starts_at', nowIso)
        .gte('ends_at', nowIso)
        .order('ends_at', { ascending: true })
        .limit(12);
      if (error) throw error;
      const rows = data || [];
      const mapped = await Promise.all(
        rows.map(async (d: any) => {
          const rawVideo = d.activity?.video_url || null;
          const signedVideo = await toPlayableVideoUrl(rawVideo);
          return {
            ...d,
            activity: d.activity
              ? {
                  id: d.activity.id,
                  title_ar: d.activity.title_ar,
                  title_en: d.activity.title_en,
                  category_id: d.activity.category_id,
                  image_url:
                    Array.isArray(d.activity.gallery_urls) && d.activity.gallery_urls.length > 0
                      ? d.activity.gallery_urls[0]
                      : null,
                  video_url: signedVideo,
                  price: Number(d.activity.price_kwd || 0),
                }
              : null,
          };
        })
      );
      return mapped as FlashDeal[];
    },
    staleTime: 60_000,
  });
}
